<template lang="pug">
Transition(name="fade")
  .popup-backdrop(
    v-if="isOpen"
    @click.self="onCancel"
  )
    .popup
      .popup-header
        .popup-header__title
        |
        .popup-header__close(@click="onCancel")
          IconX(
            type="button"
            color="#82A79B"
            width="16"
          )
      |
      .popup-content
        .left
          h2.title(v-html="block.title")
          |
          p.description(v-if="block.description" v-html="block.description")
          |
          NbButton(
            :button="block.button"
            block
          )
        |
        .right
          ImageTmp(:image="block.image")
</template>

<script setup lang="ts">
import { computed, watch, onUnmounted } from "vue";
import type { TBlock } from "@/models/block";
import { IconX } from '@tabler/icons-vue';

// --- Props ---
interface IProps {
  block?: TBlock | null;
  modelValue: boolean;
}

const props = withDefaults(defineProps<IProps>(), {
  block: null,
  modelValue: false,
});

// --- Emits ---
const emit = defineEmits(["update:modelValue", "cancel", "ok"]);

// --- Computed ---
const isOpen = computed({
  get: () => props.modelValue,
  set: (value) => emit("update:modelValue", value),
});

// --- Handlers ---
const onCancel = () => {
  isOpen.value = false;

  emit("cancel");
};

const onOk = () => {
  isOpen.value = false;

  emit("ok");
};

// --- Scroll Lock ---
watch(isOpen, (open) => {
  if (open) {
    useHead({
      htmlAttrs: { style: 'overflow: hidden;' },
      bodyAttrs: { style: 'overflow: hidden;' },
    })
  } else {
    useHead({
      htmlAttrs: { style: '' },
      bodyAttrs: { style: '' },
    })
  }
})

onUnmounted(() => {
  useHead({
    htmlAttrs: { style: '' },
    bodyAttrs: { style: '' },
  })
});
</script>

<style lang="sass" scoped>
.popup-backdrop
  position: fixed
  top: 0
  left: 0
  width: 100%
  height: 100%
  background: rgba(23, 38, 33, 0.30)
  display: flex
  justify-content: center
  align-items: center
  z-index: 1000

.popup
  width: 1048px
  max-width: 90vw
  background: var(--darkilimegreen-25, #F8FAF2)
  border-radius: 16px
  position: relative
  overflow: hidden
  display: flex
  flex-direction: column

  .popup-header
    display: flex
    justify-content: space-between
    align-items: center

    &__close
      cursor: pointer
      z-index: 10
      display: flex
      width: 24px
      height: 24px
      justify-content: center
      align-items: center
      gap: 13.333px
      aspect-ratio: 1/1
      border-radius: 1332px
      background: var(--darkilimegreen-50, #EBF0E1)
      position: absolute
      right: 12px
      top: 12px
      cursor: pointer

  .popup-content
    flex: 1 1 auto
    display: flex

    .title
      color: var(--deep-teal-800, #2C403A)
      text-align: left
      /* nbws24-h2 */
      font-size: 40px
      font-style: normal
      font-weight: 800
      line-height: 47px

    .description
      color: var(--deep-teal-800, #2C403A)
      text-align: left
      white-space: pre-line
      margin-bottom: 7px
      /* nbws24-body-lg/med */
      font-size: 18px
      font-style: normal
      font-weight: 500
      line-height: 26px

    .left, .right
      flex: 1

    .left
      display: flex
      flex-direction: column
      align-items: center
      justify-content: center
      text-align: center
      padding: 40px 48px
      gap: 24px

    .right
      img
        width: 100%
        height: 100%
        object-fit: cover

@media (max-width: 600px)
  .popup
    width: 90vw
    max-height: 100vh
    flex-direction: column
    overflow-y: auto

    .popup-header__close
      top: 16px
      right: 16px

    .popup-content
      flex-direction: column

      .left, .right
        flex: none
        width: 100%

      .left
        padding: 24px 20px
        gap: 16px

        .title
          font-size: 28px
          line-height: 36px
          text-align: center

        .description
          font-size: 16px
          line-height: 24px
          text-align: center

      .right
        order: -1
        
        img
          height: auto
          max-height: 300px
          object-fit: cover
          border-bottom: 1px solid var(--darkilimegreen-100)
</style>